import React, { useEffect, useState } from "react";
import { ExternalLink, Send, CheckCircle2 } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";

export default function RegisterPage({ embedded = false }) {
  const { t } = useTranslation();
  const { register, login, confirmTelegramCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    document.body.classList.add("register-page");
    return () => document.body.classList.remove("register-page");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError(t("auth.err_min_pass"));
      return;
    }

    setLoading(true);
    const result = await register({ name, email, password });
    setLoading(false);

    if (result.success) {
      if (result.needsConfirmation) setConfirmation({ token: result.token, botUrl: result.botUrl });
      else navigate(location.state?.from || "/dashboard");
    } else {
      setError(t(result.errorKey || "auth.user_exists"));
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{4}$/.test(verificationCode)) {
      setError(t("auth.code_invalid"));
      return;
    }
    setVerifying(true);
    const result = await confirmTelegramCode(confirmation.token, verificationCode);
    if (!result?.success) {
      setVerifying(false);
      setError(t(result?.errorKey || "auth.code_invalid"));
      return;
    }
    const loginResult = await login(email, password);
    setVerifying(false);
    if (loginResult.success) navigate(location.state?.from || "/dashboard");
    else setError(t("auth.verification_login_failed"));
  };

  return (
    <main className={`register-page flex items-center justify-center transition-colors duration-300 ${embedded ? "py-2 sm:py-4" : "min-h-[calc(100svh-68px)] bg-[#F7F6F3] p-3 dark:bg-slate-950 sm:p-6"}`}>
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-5 sm:p-8 max-w-md w-full shadow-lg">
        <h2 className="font-serif text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 text-center mb-1.5 sm:mb-2">
          {t("auth.register_title", "Регистрация")}
        </h2>
        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 text-center mb-4 sm:mb-6">
          {t("auth.register_subtitle", "Создайте аккаунт для путешествий")}
        </p>

        {error && (
          <div className="mb-3 sm:mb-4 p-2.5 sm:p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-xs font-semibold text-center">
            {error}
          </div>
        )}

        {confirmation ? <div className="rounded-2xl bg-amber-50 dark:bg-amber-400/10 border border-amber-200 dark:border-amber-400/20 p-4 sm:p-6 text-center">
          <div className="mx-auto mb-3 sm:mb-4 h-12 w-12 sm:h-14 sm:w-14 rounded-2xl bg-amber-400 text-slate-950 flex items-center justify-center"><Send size={24} /></div>
          <h3 className="font-bold text-lg text-slate-900 dark:text-white mb-2">{t("auth.telegram_code_title")}</h3>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 mb-4 sm:mb-5">{t("auth.telegram_confirm_text")}</p>
          <a href={confirmation.botUrl} target="_blank" rel="noreferrer" className="w-full bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold py-3 px-4 rounded-2xl shadow-md transition-all flex items-center justify-center gap-2">
            {t("auth.open_telegram")} <ExternalLink size={18} />
          </a>
          <form onSubmit={handleCodeSubmit} className="mt-4 sm:mt-5 space-y-2.5 sm:space-y-3">
            <label htmlFor="telegram-code" className="block text-xs font-bold text-slate-600 dark:text-slate-300">{t("auth.code_label")}</label>
            <input id="telegram-code" inputMode="numeric" autoComplete="one-time-code" maxLength={4} value={verificationCode} onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))} placeholder="0000" className="w-full bg-white dark:bg-slate-800 border border-amber-200 dark:border-slate-700 rounded-2xl px-4 py-3 text-center text-2xl font-bold tracking-[0.35em] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-400" />
            <button type="submit" disabled={verifying || verificationCode.length !== 4} className="w-full bg-slate-950 dark:bg-amber-400 dark:text-slate-950 disabled:opacity-50 text-white font-semibold py-3 rounded-2xl transition-all flex items-center justify-center gap-2">
              <CheckCircle2 size={18} /> {verifying ? t("auth.loading") : t("auth.confirm_code")}
            </button>
          </form>
        </div> : <form onSubmit={handleSubmit} className="flex flex-col gap-3 sm:gap-4">
          <div>
            <label className="text-xs font-bold text-slate-600 dark:text-slate-300 mb-1 block">
              {t("auth.name_label")}
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("auth.name_placeholder")}
              className="w-full bg-[#FAF8F5] dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl px-4 py-2.5 sm:py-3 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-slate-600 dark:text-slate-300 mb-1 block">
              {t("auth.email_label")}
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("auth.email_placeholder")}
              className="w-full bg-[#FAF8F5] dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl px-4 py-2.5 sm:py-3 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-slate-600 dark:text-slate-300 mb-1 block">
              {t("auth.password_label")}
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("auth.password_placeholder")}
              className="w-full bg-[#FAF8F5] dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl px-4 py-2.5 sm:py-3 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>

          <button
            type="submit" disabled={loading}
            className="w-full mt-1 sm:mt-2 bg-amber-400 hover:bg-amber-300 active:scale-[0.98] text-slate-950 font-bold py-2.5 sm:py-3 rounded-2xl shadow-md transition-all text-sm"
          >
            {loading ? t("auth.loading") : t("auth.register_btn", "Зарегистрироваться")}
          </button>
        </form>}

        <p className="mt-3 sm:mt-6 text-center text-xs text-slate-500 dark:text-slate-400">
          {t("auth.has_account", "Уже есть аккаунт?")}{" "}
          <Link to="/login" className="text-amber-600 dark:text-amber-400 font-bold hover:underline">
            {t("auth.login_link", "Войти")}
          </Link>
        </p>
      </div>
    </main>
  );
}
